import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { RootState } from '@/store';
import { RoleEnum } from '@/types';
import type { SupportActionLog, SupportShift, SupportShiftReport, SupportShiftViewer } from '@/types/supportShift';
import { supportShiftsService, type SupportActionsParams } from '@/services/supportShifts/supportShifts.service';
import {
  hasSupportShiftFullAccess,
  isSupportShiftReadonlyViewer,
} from '@/auth/supportShiftAccess';

const monthStart = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};

const monthEnd = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  return new Date(`${value.endsWith('Z') ? value : `${value}Z`}`).toLocaleString('ru-RU', {
    timeZone: 'Europe/Moscow',
  });
};

const formatHours = (seconds: number) => `${(seconds / 3600).toFixed(2)} ч`;

const actionLabels: Record<string, string> = {
  appeal_resolve_success: 'Закрыл апелляцию успешно',
  appeal_reject: 'Закрыл апелляцию с отклонением',
  appeal_auto_close_toggle: 'Изменил автозакрытие апелляции',
  payout_requisites_verdict: 'Вынес вердикт по выплате',
  out_payout_success: 'Закрыл выплату успешно',
  out_payout_decline: 'Закрыл выплату с отклонением',
};

const formatAction = (actionType: string) => actionLabels[actionType] || actionType;

const SupportShifts: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isAdmin = Boolean(currentUser?.is_superuser || currentUser?.role === RoleEnum.ADMIN);
  const isReadonlyShiftViewer = isSupportShiftReadonlyViewer(currentUser);
  const hasFullShiftAccess = hasSupportShiftFullAccess(currentUser);
  const isSupportOperator = Boolean(
    currentUser?.role === RoleEnum.SUPPORT && !currentUser?.is_superuser,
  );
  const isClientManager = currentUser?.role === RoleEnum.CLIENT_MANAGER;
  const canViewShiftReport = isAdmin || isReadonlyShiftViewer || hasFullShiftAccess || isSupportOperator || isClientManager;
  const canEditShiftReport = isAdmin || hasFullShiftAccess;
  const canEditShiftReason = canEditShiftReport || isClientManager;
  const canViewActions = canEditShiftReport;
  const canSelectSupport = !isSupportOperator || isReadonlyShiftViewer || hasFullShiftAccess;
  const canExportReport = isAdmin || hasFullShiftAccess;

  const [currentShift, setCurrentShift] = useState<SupportShift | null>(null);
  const [report, setReport] = useState<SupportShiftReport | null>(null);
  const [supports, setSupports] = useState<SupportShiftViewer[]>([]);
  const [supportId, setSupportId] = useState('');
  const [startDate, setStartDate] = useState(monthStart());
  const [endDate, setEndDate] = useState(monthEnd());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actions, setActions] = useState<SupportActionLog[]>([]);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [actionSearchId, setActionSearchId] = useState('');
  const [actionGroup, setActionGroup] = useState('');
  const [actionsLoading, setActionsLoading] = useState(false);
  const [salaryDrafts, setSalaryDrafts] = useState<Record<string, string>>({});
  const [minHours, setMinHours] = useState('');
  const [maxHours, setMaxHours] = useState('');
  const [stopReason, setStopReason] = useState('');

  const reportParams = useMemo(
    () => ({
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      support_id: canSelectSupport ? supportId || undefined : undefined,
      min_hours: minHours ? Number(minHours) : undefined,
      max_hours: maxHours ? Number(maxHours) : undefined,
    }),
    [canSelectSupport, startDate, endDate, supportId, minHours, maxHours],
  );

  const loadCurrent = async () => {
    const data = await supportShiftsService.getCurrent();
    setCurrentShift(data);
  };

  const loadReport = async () => {
    const data = await supportShiftsService.getReport(reportParams);
    setReport(data);
    setSalaryDrafts(
      Object.fromEntries(data.items.map((item) => [item.id, String(item.salary_amount ?? 0)])),
    );
  };

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      if (canViewShiftReport) {
        if (canSelectSupport) {
          setSupports(await supportShiftsService.getSupports());
        }
        await loadReport();
      }
      if (isSupportOperator) {
        await loadCurrent();
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Ошибка загрузки смен');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleStart = async () => {
    setError('');
    try {
      const shift = await supportShiftsService.start();
      setCurrentShift(shift);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Не удалось открыть смену');
    }
  };

  const handleStop = async () => {
    setError('');
    try {
      await supportShiftsService.stop(stopReason.trim() || undefined);
      setCurrentShift(null);
      setStopReason('');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Не удалось закрыть смену');
    }
  };

  const handleSalarySave = async (shiftId: string) => {
    await supportShiftsService.updateSalary(shiftId, salaryDrafts[shiftId] || '0');
    await loadReport();
  };

  const handleValidityChange = async (shiftId: string, isValid: boolean) => {
    setError('');
    try {
      await supportShiftsService.updateValidity(shiftId, isValid);
      await loadReport();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Не удалось обновить статус смены');
    }
  };

  const openActions = async (shiftId: string) => {
    const data = await supportShiftsService.getActions({ shift_id: shiftId });
    setActions(data);
    setActionsOpen(true);
  };

  const loadActions = async () => {
    setActionsLoading(true);
    setError('');
    try {
      const data = await supportShiftsService.getActions({
        support_id: canSelectSupport ? supportId || undefined : undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        search_id: actionSearchId.trim() || undefined,
        action_group: (actionGroup || undefined) as SupportActionsParams['action_group'],
      });
      setActions(data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Не удалось загрузить действия саппортов');
    } finally {
      setActionsLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, value: number) => {
    setActiveTab(value);
    if (value === 1 && actions.length === 0) {
      void loadActions();
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Смены саппортов
      </Typography>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {isSupportOperator && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="text.secondary">Текущая смена</Typography>
              <Typography variant="h6">
                {currentShift ? `${currentShift.shift_type} с ${formatDateTime(currentShift.started_at)}` : 'Не открыта'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Смену можно начать и закончить в любое время.
              </Typography>
            </Grid>
            <Grid item xs={12} md={8} display="flex" gap={2} justifyContent="flex-end">
              <Button variant="contained" disabled={Boolean(currentShift) || loading} onClick={handleStart}>
                Начать смену
              </Button>
              <Button variant="outlined" color="error" disabled={!currentShift || loading} onClick={handleStop}>
                Закончить смену
              </Button>
            </Grid>
          </Grid>
          {currentShift && <TextField fullWidth sx={{ mt: 2 }} label="Причина опоздания" value={stopReason} onChange={(event) => setStopReason(event.target.value)} helperText="При завершении смены раньше 11,5 часов причина обязательна." inputProps={{ maxLength: 1000 }} />}
        </Paper>
      )}

      {canViewShiftReport && (
        <Paper sx={{ mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="Разделы смен саппортов">
            <Tab label="Смены" />
            {canViewActions && <Tab label="Действия" />}
          </Tabs>
        </Paper>
      )}

      {canViewShiftReport && activeTab === 0 && <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {canSelectSupport && <Grid item xs={12} md={3}>
            <TextField
              label="Саппорт"
              select
              fullWidth
              value={supportId}
              onChange={(e) => setSupportId(e.target.value)}
            >
              <MenuItem value="">Все</MenuItem>
              {supports.map((support) => (
                <MenuItem key={support.id} value={support.id}>
                  {support.username || support.email}
                </MenuItem>
              ))}
            </TextField>
          </Grid>}
          <Grid item xs={12} md={2}>
            <TextField label="С" type="date" fullWidth value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField label="По" type="date" fullWidth value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={2}><TextField fullWidth type="number" label="Время от, ч" value={minHours} onChange={(event) => setMinHours(event.target.value)} inputProps={{ min: 0, step: 0.5 }} /></Grid>
          <Grid item xs={12} md={2}><TextField fullWidth type="number" label="Время до, ч" value={maxHours} onChange={(event) => setMaxHours(event.target.value)} inputProps={{ min: 0, step: 0.5 }} /></Grid>
          <Grid item xs={12} md={canSelectSupport ? 5 : 8} display="flex" gap={1} justifyContent="flex-end">
            <Button variant="contained" onClick={loadReport}>Применить</Button>
            {canExportReport && <Button startIcon={<DownloadIcon />} onClick={() => supportShiftsService.exportReport({ ...reportParams, format: 'xlsx' })}>XLSX</Button>}
            {canExportReport && <Button startIcon={<DownloadIcon />} onClick={() => supportShiftsService.exportReport({ ...reportParams, format: 'csv' })}>CSV</Button>}
          </Grid>
        </Grid>
      </Paper>}

      {canViewShiftReport && activeTab === 0 && report && (
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} md={2}><Paper sx={{ p: 2 }}><Typography>Валидных смен</Typography><Typography variant="h5">{report.total_valid_shifts}</Typography></Paper></Grid>
          <Grid item xs={12} md={2}><Paper sx={{ p: 2 }}><Typography>Часы</Typography><Typography variant="h5">{formatHours(report.total_worked_seconds)}</Typography></Paper></Grid>
          {report.can_view_salary && <Grid item xs={12} md={2}><Paper sx={{ p: 2 }}><Typography>ЗП</Typography><Typography variant="h5">{Number(report.total_salary || 0).toFixed(2)}</Typography></Paper></Grid>}
          <Grid item xs={12} md={report.can_view_salary ? 3 : 4}><Paper sx={{ p: 2 }}>
            <Typography>Апелляции</Typography><Typography variant="h5">{report.total_appeals_closed}</Typography>
            <Typography variant="body2" color="text.secondary">
              Фейки: {report.total_appeals_fake_closed ?? '—'} · Остальные: {report.total_appeals_other_closed ?? '—'}
            </Typography>
          </Paper></Grid>
          <Grid item xs={12} md={report.can_view_salary ? 3 : 4}><Paper sx={{ p: 2 }}><Typography>Выплаты</Typography><Typography variant="h5">{report.total_payouts_closed}</Typography></Paper></Grid>
        </Grid>
      )}

      {canViewShiftReport && activeTab === 0 && report && (
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 2, height: 360 }}>
              <Typography variant="h6" mb={2}>Закрыто саппортом по дням</Typography>
              <ResponsiveContainer width="100%" height="88%">
                <BarChart data={report.daily_statistics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(value: string) => value.slice(5)} />
                  <YAxis allowDecimals={false} />
                  <ChartTooltip />
                  <Legend />
                  <Bar dataKey="appeals_other_closed" name="Апелляции: остальные" stackId="appeals" fill="#66bb6a" />
                  <Bar dataKey="appeals_fake_closed" name="Апелляции: фейки" stackId="appeals" fill="#ef5350" />
                  <Bar dataKey="payouts_succeeded" name="Выплаты: успех" stackId="payouts" fill="#42a5f5" />
                  <Bar dataKey="payouts_declined" name="Выплаты: отказ" stackId="payouts" fill="#ff9800" />
                  <Bar dataKey="requisites_verdicts" name="Вердикты реквизитов" stackId="payouts" fill="#ab47bc" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 2, height: 360 }}>
              <Typography variant="h6" mb={2}>Объём транзакций по дням</Typography>
              <ResponsiveContainer width="100%" height="88%">
                <LineChart data={report.daily_statistics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(value: string) => value.slice(5)} />
                  <YAxis allowDecimals={false} />
                  <ChartTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="transactions_in" name="IN" stroke="#66bb6a" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="transactions_out" name="OUT" stroke="#42a5f5" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, height: 380 }}>
              <Typography variant="h6" mb={0.5}>Остаток активных выплат</Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Снимки на 08:00 и 20:00 МСК. Будущие даты не заполняются.
              </Typography>
              <ResponsiveContainer width="100%" height="82%">
                <LineChart data={report.daily_statistics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(value: string) => value.slice(5)} />
                  <YAxis allowDecimals={false} />
                  <ChartTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="active_payouts_at_08" name="Выплаты 08:00" stroke="#42a5f5" strokeWidth={2} connectNulls={false} />
                  <Line type="monotone" dataKey="active_payouts_at_20" name="Выплаты 20:00" stroke="#5e35b1" strokeWidth={2} connectNulls={false} />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {canViewShiftReport && activeTab === 0 && <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Саппорт</TableCell>
              <TableCell>Смена</TableCell>
              <TableCell>Старт</TableCell>
              <TableCell>Финиш</TableCell>
              <TableCell>Время</TableCell>
              <TableCell>Причина опоздания</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Апелляции</TableCell>
              <TableCell>Фейки</TableCell>
              <TableCell>Остальные</TableCell>
              <TableCell>Payouts</TableCell>
              {report?.can_view_salary && <TableCell>ЗП</TableCell>}
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(report?.items || []).map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.support_username || row.support_email || row.support_id}</TableCell>
                <TableCell>{row.shift_type}</TableCell>
                <TableCell>{formatDateTime(row.started_at)}</TableCell>
                <TableCell>{formatDateTime(row.ended_at)}</TableCell>
                <TableCell>{formatHours(row.worked_seconds)}</TableCell>
                <TableCell>
                  {row.ended_at && row.worked_seconds < 41400 ? row.short_shift_reason || 'Не указана' : '—'}
                  {canEditShiftReason && row.ended_at && row.worked_seconds < 41400 && <Button size="small" onClick={async () => {
                    const reason = window.prompt('Причина опоздания', row.short_shift_reason || '');
                    if (!reason?.trim()) return;
                    try { await supportShiftsService.updateReason(row.id, reason.trim()); await loadReport(); }
                    catch { setError('Не удалось сохранить причину'); }
                  }}>Указать причину</Button>}
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5} alignItems="center">
                    <Chip size="small" color={row.is_valid ? 'success' : 'warning'} label={row.is_valid ? 'Засчитана' : 'Не засчитана'} />
                    {canEditShiftReport && (
                      <Tooltip title={row.is_valid ? 'Не засчитать смену' : 'Засчитать смену'}>
                        <IconButton
                          size="small"
                          color={row.is_valid ? 'warning' : 'success'}
                          onClick={() => handleValidityChange(row.id, !row.is_valid)}
                          sx={{
                            border: '1px solid',
                            borderColor: row.is_valid ? 'warning.main' : 'success.main',
                            width: 28,
                            height: 28,
                          }}
                        >
                          {row.is_valid ? <CancelIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{row.appeals_closed}</TableCell>
                <TableCell>{row.appeals_fake_closed ?? '—'}</TableCell>
                <TableCell>{row.appeals_other_closed ?? '—'}</TableCell>
                <TableCell>{row.payouts_closed}</TableCell>
                {report?.can_view_salary && <TableCell>
                  {canEditShiftReport ? (
                    <Box display="flex" gap={1}>
                      <TextField
                        size="small"
                        value={salaryDrafts[row.id] || '0'}
                        onChange={(e) => setSalaryDrafts((prev) => ({ ...prev, [row.id]: e.target.value }))}
                        sx={{ width: 110 }}
                      />
                      <Button size="small" onClick={() => handleSalarySave(row.id)}>OK</Button>
                    </Box>
                  ) : Number(row.salary_amount).toFixed(2)}
                </TableCell>}
                <TableCell>
                  {canViewActions && (
                    <IconButton size="small" onClick={() => openActions(row.id)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>}

      {canViewActions && activeTab === 1 && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              {canSelectSupport && <Grid item xs={12} md={3}>
                <TextField
                  label="Саппорт"
                  select
                  fullWidth
                  value={supportId}
                  onChange={(e) => setSupportId(e.target.value)}
                >
                  <MenuItem value="">Все саппорты</MenuItem>
                  {supports.map((support) => (
                    <MenuItem key={support.id} value={support.id}>
                      {support.username || support.email}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>}
              <Grid item xs={12} sm={6} md={2}>
                <TextField label="С" type="date" fullWidth value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField label="По" type="date" fullWidth value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  label="ID апелляции, выплаты или транзакции"
                  fullWidth
                  value={actionSearchId}
                  onChange={(e) => setActionSearchId(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void loadActions(); }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Действие"
                  select
                  fullWidth
                  value={actionGroup}
                  onChange={(e) => setActionGroup(e.target.value)}
                >
                  <MenuItem value="">Все действия</MenuItem>
                  <MenuItem value="appeal_closed">Закрыл апелляцию</MenuItem>
                  <MenuItem value="appeal_fake">Закрыл апелляцию как фейк</MenuItem>
                  <MenuItem value="appeal_other">Закрыл апелляцию: остальные</MenuItem>
                  <MenuItem value="payout_closed">Закрыл выплату</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={8} display="flex" gap={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSupportId('');
                    setStartDate(monthStart());
                    setEndDate(monthEnd());
                    setActionSearchId('');
                    setActionGroup('');
                  }}
                >
                  Сбросить
                </Button>
                <Button variant="contained" disabled={actionsLoading} onClick={loadActions}>
                  {actionsLoading ? 'Загрузка...' : 'Применить'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Время</TableCell>
                  <TableCell>Саппорт</TableCell>
                  <TableCell>Действие</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell>ID апелляции / выплаты</TableCell>
                  <TableCell>ID транзакции</TableCell>
                  <TableCell>Результат</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!actionsLoading && actions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">Действия не найдены</TableCell>
                  </TableRow>
                ) : actions.map((action) => (
                  <TableRow key={action.id} hover>
                    <TableCell>{formatDateTime(action.created_at)}</TableCell>
                    <TableCell>{action.support_username || action.support_email || action.support_id}</TableCell>
                    <TableCell>{formatAction(action.action_type)}</TableCell>
                    <TableCell>{action.entity_type === 'appeal' ? 'Апелляция' : 'Выплата'}</TableCell>
                    <TableCell>{action.entity_id}</TableCell>
                    <TableCell>{action.transaction_id || '-'}</TableCell>
                    <TableCell><Chip size="small" label={action.result} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={actionsOpen} onClose={() => setActionsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Действия смены</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Время</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>Сущность</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Транзакция</TableCell>
                <TableCell>Результат</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {actions.map((action) => (
                <TableRow key={action.id}>
                  <TableCell>{formatDateTime(action.created_at)}</TableCell>
                  <TableCell>{formatAction(action.action_type)}</TableCell>
                  <TableCell>{action.entity_type}</TableCell>
                  <TableCell>{action.entity_id}</TableCell>
                  <TableCell>{action.transaction_id || '-'}</TableCell>
                  <TableCell>{action.result}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SupportShifts;
